import { useState, useEffect, useMemo } from 'react';
import FilterDropdown from '../../../common/FilterDropdown';
import { Booklet, BookletCompanyReview, Company, Tag } from 'types/generated.types';
import { useCompaniesTableContext } from '../../../context/CompaniesTableContext';
import { useQuery } from '@apollo/client';
import { getTagsForZoneQuery } from '../../../queries/tags';

interface Props {
  activeBooklets: Booklet[];
  companies: Company[];
  reviews?: BookletCompanyReview[];
  zoneId: string;
  setFilteredCompanies: (filteredCompanies: Company[]) => void;
}

type FilterType =
  | 'on'
  | 'off'
  | 'no-coupons'
  | 'has-coupons'
  | 'no-contact'
  | 'barcode'
  | 'no-tags'
  | 'active-review-process'
  | 'no-review'
  | 'review-requested'
  | 'review-comment'
  | 'review-approved';

function FilterCompaniesSection({ activeBooklets, companies, reviews, zoneId, setFilteredCompanies }: Props) {
  const { companyCoupons } = useCompaniesTableContext();
  const [filters, setFilters] = useState<FilterType[]>([]);

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const { data: tagsData, loading } = useQuery(getTagsForZoneQuery, {
    variables: { zoneId },
    skip: !zoneId,
  });

  const tagOptions = useMemo(() => {
    const allTags: Tag[] = tagsData?.tags ?? [];
    return allTags.map(tag => ({
      value: tag._id,
      label: tag.name,
    }));
  }, [tagsData?.tags]);

  // Get all active coupon IDs from active booklets
  const activeCouponIds = useMemo(() => {
    const ids: string[] = [];
    activeBooklets.forEach(booklet => {
      booklet.couponIds.forEach(id => ids.push(id));
    });
    return ids;
  }, [activeBooklets]);

  // Helper function to check if a company has an active coupon
  const hasActiveCoupon = (companyId: string): boolean => {
    const coupons = companyCoupons[companyId];
    if (!coupons || !Array.isArray(coupons)) return false;

    return coupons.some(coupon =>
      activeCouponIds.includes(coupon._id)
    );
  };

  // Helper function to check if a company has any coupons
  const hasAnyCoupons = (companyId: string): boolean => {
    const coupons = companyCoupons[companyId];
    return coupons && Array.isArray(coupons) && coupons.length > 0;
  };

  // Helper function to check if a company is missing any contact info
  const missingContactInfo = (company: Company): boolean => {
    return company.contactEmails?.length === 0 || !company.contactMobile;
  };

  // Helper function to check if a company has a coupon with a barcode
  const hasBarcode = (companyId: string): boolean => {
    const coupons = companyCoupons[companyId];
    if (!coupons || !Array.isArray(coupons)) return false;

    return coupons.some(coupon =>
      coupon.codeType && coupon.codeType !== 'TEXT'
    );
  };

  // Helper function to check tags (any selected tag exists)
  const hasTags = (company: Company, tagIds: string[]) => {
    if (!tagIds.length) return true;

    const companyTagIds =
      company.tagIds ||
      company.tags?.map(t => t._id) ||
      [];

    return tagIds.some(id => companyTagIds.includes(id));
  };

  // Helper function to get review status for a company
  const getReviewStatus = (companyId: string) => {
    const defaultStatus = {
      hasReview: false,
      isApproved: false,
      hasComment: false,
      isRequested: false,
      isActive: false,
      status: 'none' as const
    };

    if (!reviews) {
      return defaultStatus;
    }
    const activeBookletIds = new Set(
      activeBooklets.map(b => b._id.toString())
    );

    const companyReviews = reviews?.filter(review => {
      return (
        review.company._id.toString() === companyId &&
        activeBookletIds.has(review.bookletId.toString()) &&
        review.isActive
      );
    });

    // Get the most recent review (if any)
    const latestReview = companyReviews?.sort((a, b) => {
      const dateA = a.sentDate ? new Date(a.sentDate).getTime() : 0;
      const dateB = b.sentDate ? new Date(b.sentDate).getTime() : 0;
      return dateB - dateA;
    })[0];

    if (!latestReview) {
      return defaultStatus;
    }

    const isApproved = !!latestReview.approvedDate;
    const hasComment = !!latestReview.comment && !latestReview.approvedDate;
    const isRequested = !!latestReview.sentToEmails?.length && !latestReview.comment && !latestReview.approvedDate;
    const isActive = !!latestReview.isActive;

    let status: 'none' | 'requested' | 'commented' | 'approved' = 'none';

    if (isApproved) {
      status = 'approved';
    } else if (hasComment) {
      status = 'commented';
    } else if (isRequested) {
      status = 'requested';
    }

    return {
      hasReview: true,
      isApproved,
      hasComment,
      isRequested,
      isActive,
      status
    };
  };

  // Apply all active filters to companies
  const applyFilters = useMemo(() => {
    return (company: Company): boolean => {
      // If no filters are active, show all companies
      if (filters.length === 0 && selectedTagIds.length === 0) return true;

      const status = getReviewStatus(company._id.toString());
      // Check each filter condition
      const conditions = filters.map(filter => {
        switch (filter) {
          case 'on':
            return hasActiveCoupon(company._id.toString());

          case 'off':
            // Company has coupons but none are active
            return hasAnyCoupons(company._id.toString()) && !hasActiveCoupon(company._id.toString());

          case 'no-coupons':
            // Company has no coupons at all
            return !hasAnyCoupons(company._id.toString());

          case 'has-coupons':
            // Company has any coupons (active or inactive)
            return hasAnyCoupons(company._id.toString());

          case 'no-contact':
            // Company lacks any contact-info
            return missingContactInfo(company);

          case 'barcode':
            // Company has barcode coupons
            return hasBarcode(company._id.toString());

          case 'no-tags':
            // Company has no tags
            return !company.tags || company.tags.length === 0;

          case 'active-review-process':
            return status.isActive;

          case 'no-review':
            return !status?.hasReview || status?.status === 'none';

          case 'review-requested':
            return status?.status === 'requested' && status.isActive;

          case 'review-comment':
            return status?.status === 'commented';

          case 'review-approved':
            return status?.status === 'approved';

          default:
            return true;
        }
      });

      // Tag filtering (AND)
      const tagCondition = hasTags(company, selectedTagIds);

      // All active filters must be satisfied (AND logic)
      return conditions.every(condition => condition === true) && tagCondition;
    };
  }, [filters, selectedTagIds, companyCoupons, activeCouponIds]);

  // Update filtered companies when filters or active booklets change
  useEffect(() => {
    const filtered = companies.filter(applyFilters);
    setFilteredCompanies(filtered);
  }, [filters, selectedTagIds, activeBooklets, companies, applyFilters]);

  const update = (newFilters: FilterType[]) => {
    setFilters(newFilters);
  };

  return (
    <section className="flex gap-2" style={{ zIndex: '10', position: 'sticky', top: '20px' }}>
      <FilterDropdown
        name="filters"
        value={filters}
        onChange={(vals) => update(vals as FilterType[])}
        options={[
          { value: 'on', label: 'Har aktiva kuponger' },
          { value: 'off', label: 'Har inaktiva kuponger' },
          { value: 'no-coupons', label: 'Har inga kuponger' },
          { value: 'has-coupons', label: 'Har kuponger' },
          { value: 'no-contact', label: 'Företag saknar kontaktuppgift' },
          { value: 'barcode', label: 'Har streckkods kuponger' },
          { value: 'no-tags', label: 'Har inga taggar' },
          { value: 'active-review-process', label: 'Aktiv granskningsprocess' },
          { value: 'no-review', label: 'Har ingen aktuell granskning' },
          { value: 'review-requested', label: 'Granskningförfrågan skickad' },
          { value: 'review-comment', label: 'Granskning kommenterad' },
          { value: 'review-approved', label: 'Granskning godkänd' },
        ]}
        placeholder="Filter..."
        tooltip="Filter för valda häften"
      />

      {(!loading && tagOptions) &&
        <FilterDropdown
          name="tags"
          value={selectedTagIds}
          onChange={setSelectedTagIds}
          options={tagOptions}
          placeholder="Taggar..."
          tooltip="Filtrera på taggar"
        />
      }
    </section>
  );
}

export default FilterCompaniesSection;
